// Apolo IDE — radar_emit: the authenticated agent→Radar write bridge (radar-emit sprint).
//
// THE GAP IT CLOSES: Hermes radar agents run on the Mac with tools, but the
// shared tool surface (run_command/read_file/…) has no authenticated way to
// write into the Radar API — run_command carries no JWT, so scans completed
// and the feed stayed empty. This tool executes in the RENDERER, where the
// radarApi client is already authenticated as the signed-in user (apiFetch),
// so an agent can persist what it found without any secret in its sandbox.
//
// Shape: ONE tool, `kind`-discriminated (keeps the tool surface small for
// small local models):
//   finding           → radarApi.createFinding  (the feed card; upsert by dedupe)
//   finding_update    → radarApi.patchFinding   (Curador: status/score/lens)
//   source_health     → radarApi.updateSource   (mark a source ok|stale|failing)
//   quarantine        → radarApi.createQuarantine (queue an installable for scanning)
//   apolo_improvement → radarApi.createFinding  (apolo-improver E2): upserts the
//     SAME radar_findings row but flagged as a self-improvement PROPOSAL
//     (is_proposal=1, install_state='proposed') carrying the install metadata
//     (proposalSummary / installKind / installTarget) the Runs-tab proposal card
//     and the install flow read. The Curador emits one per top-5 Apolo-improving
//     repo it finds; the row dedupes by url/title like any finding.
// Every outcome — success, validation miss, API failure — returns a STRING for
// the model (the loop's tool-result contract); it never throws.
//
// Injected via the agentLoop `extraTools` seam by appExecutionAdapter (every
// Hermes run gets it). `mutating: true` so a future 'ask'-mode run still gates
// it behind approval; Hermes runs are 'ignore' (autonomous) today.

import { radarApi as realRadarApi } from '../../radar/radarApiClient.js';

const HEALTH = new Set(['ok', 'stale', 'failing']);
const INSTALL_KIND = new Set(['skill', 'plugin', 'code-pr']);

export const RADAR_EMIT_DEF = {
  type: 'function',
  function: {
    name: 'radar_emit',
    description:
      'Grava um resultado do Radar (autenticado como o usuário — use SEMPRE isto em vez de curl/POST manual). ' +
      'kind="finding": registra um achado no feed (title obrigatório; url/summary/sourceId/tags opcionais). ' +
      'kind="finding_update": atualiza um achado existente — dedupe (status="dismissed"), score (S|A|B|C) e lente (findingId obrigatório). ' +
      'kind="source_health": marca a saúde de uma fonte (sourceId e health ok|stale|failing obrigatórios). ' +
      'kind="quarantine": enfileira uma skill/repo instalável pra quarentena (skillName obrigatório; repoUrl/findingId opcionais). ' +
      'kind="apolo_improvement": registra uma PROPOSTA de melhoria do próprio Apolo (title + proposalSummary + installKind skill|plugin|code-pr + installTarget obrigatórios; url/summary/score/lens/tags opcionais) — vira card de Propostas na aba Runs pra Pedro Instalar/Dispensar. ' +
      'Ex: {"kind":"finding","title":"anthropics/skills — packs de skills oficiais","url":"https://github.com/anthropics/skills","summary":"Repo oficial com 50+ skills; gate 7/7 OK","tags":["skill","ia"]}',
    parameters: {
      type: 'object',
      properties: {
        kind: { type: 'string', enum: ['finding', 'finding_update', 'source_health', 'quarantine', 'apolo_improvement'] },
        title: { type: 'string', description: 'finding/apolo_improvement: título curto do achado/proposta' },
        url: { type: 'string', description: 'finding/apolo_improvement: link do repo/post' },
        summary: { type: 'string', description: 'finding/apolo_improvement: o que é + por que reaproveitar + veredito do gate' },
        sourceId: { type: 'string', description: 'finding/source_health: id da fonte de origem' },
        tags: { type: 'array', items: { type: 'string' }, description: 'finding/apolo_improvement: tema, plataforma, skill/repo' },
        findingId: { type: 'string', description: 'finding_update/quarantine: id do achado' },
        status: { type: 'string', enum: ['new', 'saved', 'tasked', 'dispatched', 'dismissed'], description: 'finding_update: status (dismissed = dedupe)' },
        score: { type: 'string', enum: ['S', 'A', 'B', 'C'], description: 'finding_update/apolo_improvement: nota do Curador' },
        lens: {
          type: 'object',
          description: 'finding_update/apolo_improvement: { becomes (no que vira), product (Apolo|Glyco|outro), effort (P|M|G) }',
          properties: { becomes: { type: 'string' }, product: { type: 'string' }, effort: { type: 'string' } },
        },
        health: { type: 'string', enum: ['ok', 'stale', 'failing'], description: 'source_health: estado da fonte' },
        lastError: { type: 'string', description: 'source_health: erro curto quando failing' },
        skillName: { type: 'string', description: 'quarantine: nome da skill/repo' },
        repoUrl: { type: 'string', description: 'quarantine: url do repo' },
        proposalSummary: { type: 'string', description: 'apolo_improvement: o que melhora no Apolo + por quê (texto do card)' },
        installKind: { type: 'string', enum: ['skill', 'plugin', 'code-pr'], description: 'apolo_improvement: como instala — skill|plugin (via /api/plugins) ou code-pr (dispatch → PR)' },
        installTarget: { type: 'string', description: 'apolo_improvement: alvo da instalação — url do repo / nome da skill / pacote' },
      },
      required: ['kind'],
    },
  },
};

// G4-WS1-T01 — the execute() dispatcher was one cognitive-65 function (5 kind branches
// inline). Each kind is now a module-private handler (args, api) → string; execute is a
// tiny dispatch-table lookup wrapped in the ONE shared try/catch. Byte-identical: same
// validations, same api calls, same strings, same never-throws contract.

async function handleFinding(args, api) {
  const title = String(args.title || '').trim();
  if (!title) return 'Erro radar_emit: finding exige title (string não-vazia).';
  const finding = await api.createFinding({
    sourceId: args.sourceId || undefined,
    title,
    url: args.url || undefined,
    summary: args.summary || undefined,
    tags: Array.isArray(args.tags) ? args.tags : undefined,
  });
  return `Achado gravado no feed do Radar (id=${finding?.id || '?'}, score=${finding?.score || 'C'}). O Curador pontua e mapeia a lente depois.`;
}

async function handleFindingUpdate(args, api) {
  const findingId = String(args.findingId || '').trim();
  if (!findingId) return 'Erro radar_emit: finding_update exige findingId.';
  const patch = {};
  if (args.status) patch.status = args.status;
  if (args.score) patch.score = args.score;
  if (args.lens && typeof args.lens === 'object') patch.lens = args.lens;
  if (!Object.keys(patch).length) return 'Erro radar_emit: finding_update exige ao menos status, score ou lens.';
  const finding = await api.patchFinding(findingId, patch);
  return `Achado ${findingId} atualizado (score=${finding?.score || '?'}, status=${finding?.status || '?'}).`;
}

async function handleSourceHealth(args, api) {
  const sourceId = String(args.sourceId || '').trim();
  const health = String(args.health || '').trim();
  if (!sourceId) return 'Erro radar_emit: source_health exige sourceId.';
  if (!HEALTH.has(health)) return 'Erro radar_emit: health deve ser ok, stale ou failing.';
  await api.updateSource(sourceId, { health });
  return `Fonte ${sourceId} marcada como ${health}.${health === 'failing' && args.lastError ? ` Motivo registrado: ${String(args.lastError).slice(0, 200)}` : ''}`;
}

async function handleQuarantine(args, api) {
  const skillName = String(args.skillName || '').trim();
  if (!skillName) return 'Erro radar_emit: quarantine exige skillName.';
  const item = await api.createQuarantine({
    findingId: args.findingId || undefined,
    skillName,
    repoUrl: args.repoUrl || undefined,
  });
  return `Enfileirado na quarentena (id=${item?.id || '?'}, estado=${item?.state || 'pending'}). O scan roda antes de qualquer Promote.`;
}

async function handleApoloImprovement(args, api) {
  const title = String(args.title || '').trim();
  if (!title) return 'Erro radar_emit: apolo_improvement exige title (string não-vazia).';
  const installKind = String(args.installKind || '').trim();
  if (!INSTALL_KIND.has(installKind)) return 'Erro radar_emit: apolo_improvement exige installKind skill, plugin ou code-pr.';
  const installTarget = String(args.installTarget || '').trim();
  if (!installTarget) return 'Erro radar_emit: apolo_improvement exige installTarget (url do repo / nome da skill / pacote).';
  // proposalSummary is required end-to-end: the server runs normalizeProposalSummary
  // (radar/domain/proposal.js) on every proposal upsert and throws on an empty value.
  // Validate here so a model that omits it gets a clear local hint.
  const proposalSummary = String(args.proposalSummary || '').trim();
  if (!proposalSummary) return 'Erro radar_emit: apolo_improvement exige proposalSummary (o que melhora no Apolo + por quê).';
  // Upsert the SAME radar_findings row, flagged as a proposal. is_proposal and
  // install_state are owned here (proposed); dedupe still keys off url/title.
  const finding = await api.createFinding({
    sourceId: args.sourceId || undefined,
    title,
    url: args.url || undefined,
    summary: args.summary || undefined,
    score: args.score || undefined,
    lens: args.lens && typeof args.lens === 'object' ? args.lens : undefined,
    tags: Array.isArray(args.tags) ? args.tags : undefined,
    isProposal: true,
    installState: 'proposed',
    proposalSummary,
    installKind,
    installTarget,
  });
  return `Proposta de melhoria do Apolo gravada (id=${finding?.id || '?'}, installKind=${installKind}, estado=${finding?.installState || 'proposed'}). Aparece como card de Propostas na aba Runs pra Pedro Instalar ou Dispensar.`;
}

const RADAR_EMIT_HANDLERS = {
  finding: handleFinding,
  finding_update: handleFindingUpdate,
  source_health: handleSourceHealth,
  quarantine: handleQuarantine,
  apolo_improvement: handleApoloImprovement,
};

/**
 * Build the radar_emit extra-tool for the agentLoop seam.
 * @param {{ api?: object }} deps  injectable radarApi for tests
 * @returns {{ def: object, execute: (args: object) => Promise<string>, mutating: true }}
 */
export function makeRadarEmitTool({ api = realRadarApi } = {}) {
  async function execute(args = {}) {
    const kind = String(args.kind || '').trim();
    const handler = RADAR_EMIT_HANDLERS[kind];
    if (!handler) return 'Erro radar_emit: kind deve ser finding, finding_update, source_health, quarantine ou apolo_improvement.';
    try {
      return await handler(args, api);
    } catch (e) {
      return `Erro radar_emit: ${e?.message || 'falha na API do Radar'}`;
    }
  }

  return { def: RADAR_EMIT_DEF, execute, mutating: true };
}
